import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { Switch } from '~/components/ui/inputs/Switch';
import type {
  MsrIncoTermOption,
  MsrUnitTypeOption,
} from '~/api/msrPurchaseOrder';
import type { MsrBuyerCodeCandidate } from '~/api/users';
import { formatNameWithCode, getLocalDateString } from '~/utils';
import { useIncotermLabel } from '~/utils/incoterms';
import {
  effectiveBuyerCode,
  msrInputsErrors,
  type MsrPurchaseOrderInputsForm,
} from './msrPurchaseOrderInputs';

interface MsrPurchaseOrderInputsSectionProps {
  value: MsrPurchaseOrderInputsForm;
  onChange: (value: MsrPurchaseOrderInputsForm) => void;
  candidates: readonly MsrBuyerCodeCandidate[];
  selfEmployeeId: string | undefined;
  isLoading: boolean;
  isError: boolean;
  unitTypes: readonly MsrUnitTypeOption[];
  isUnitTypesLoading: boolean;
  incoTerms: readonly MsrIncoTermOption[];
  isIncoTermsLoading: boolean;
}

const MANUAL_CODE = '__manual__';
const INCO_TERMS_NONE = '__none__';

interface CodeFieldProps {
  name: string;
  label: string;
  manualLabel: string;
  manualOptionLabel: string;
  value: string;
  options: readonly { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  onChange: (code: string) => void;
}

function CodeField({
  name,
  label,
  manualLabel,
  manualOptionLabel,
  value,
  options,
  placeholder,
  disabled,
  error,
  onChange,
}: Readonly<CodeFieldProps>) {
  const [manual, setManual] = useState(false);
  const isListed = options.some((option) => option.value === value);
  const showManual = manual || (value !== '' && !isListed);
  const selectOptions = [
    ...options,
    { value: MANUAL_CODE, label: manualOptionLabel },
  ];
  const selectValue = showManual ? MANUAL_CODE : value;

  const handleSelect = (code: string) => {
    if (code === MANUAL_CODE) {
      setManual(true);
      return;
    }
    setManual(false);
    onChange(code);
  };

  return (
    <div>
      <SearchableSelect
        name={name}
        label={label}
        options={selectOptions}
        value={selectValue}
        placeholder={placeholder}
        disabled={disabled}
        error={showManual ? undefined : error}
        onChange={(_, code) => handleSelect(code)}
      />
      {showManual && (
        <div className="mt-2">
          <Input
            name={`${name}Manual`}
            type="text"
            label={manualLabel}
            value={value}
            error={error}
            onChange={(_, code) => onChange(code)}
          />
        </div>
      )}
    </div>
  );
}

const candidateLabel = (candidate: MsrBuyerCodeCandidate) =>
  formatNameWithCode(
    `${candidate.lastName}, ${candidate.firstName}`,
    candidate.employeeId
  );

export function MsrPurchaseOrderInputsSection({
  value,
  onChange,
  candidates,
  selfEmployeeId,
  isLoading,
  isError,
  unitTypes,
  isUnitTypesLoading,
  incoTerms,
  isIncoTermsLoading,
}: Readonly<MsrPurchaseOrderInputsSectionProps>) {
  const { t } = useTranslation('cart');
  const incotermLabel = useIncotermLabel();
  const base = 'cart.msr_purchase_order_inputs';
  const buyerCode = effectiveBuyerCode(value, selfEmployeeId);
  const errors = msrInputsErrors(value, selfEmployeeId);
  const options = candidates.map((candidate) => ({
    value: candidate.employeeId,
    label: candidateLabel(candidate),
  }));
  const unitTypeOptions = unitTypes.map((unitType) => ({
    value: unitType.code,
    label: formatNameWithCode(unitType.description, unitType.code),
  }));
  const unitType =
    value.unitType || unitTypes.find((option) => option.isDefault)?.code || '';
  const defaultIncoTerm = incoTerms.find((option) => option.isDefault);
  const incoTermOptions = [
    ...(defaultIncoTerm == null
      ? [{ value: INCO_TERMS_NONE, label: t(`${base}.none`) }]
      : []),
    ...incoTerms.map((option) => ({
      value: option.code,
      label: incotermLabel(option.code),
    })),
  ];
  const incoTermCode =
    value.incoTerms || (defaultIncoTerm != null ? defaultIncoTerm.code : '');
  const incoTerm = incoTerms.find((option) => option.code === incoTermCode);
  const pickIncoTerm = (code: string) => {
    const picked = incoTerms.find((option) => option.code === code);
    onChange({ ...value, incoTerms: picked != null ? picked.code : '' });
  };
  const buyerCodeError = () => {
    if (errors.buyerCode === 'too_long') return t(`${base}.code_too_long`);
    if (errors.buyerCode === 'required' && !isLoading && !isError) {
      return t(
        selfEmployeeId == null
          ? `${base}.buyer_code_missing_self`
          : `${base}.buyer_code_required`
      );
    }
    return undefined;
  };

  return (
    <div data-testid="msr-purchase-order-inputs">
      <SectionPanel title={t(`${base}.title`)}>
        <p className="text-sm text-neut-sec mb-4">{t(`${base}.description`)}</p>
        {isError && (
          <p role="alert" className="text-sm text-error-700 mb-4">
            {t(`${base}.load_failed`)}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <CodeField
              name="buyerCode"
              label={t(`${base}.buyer_code`)}
              manualLabel={t(`${base}.manual_buyer_code`)}
              manualOptionLabel={t(`${base}.enter_code_manually`)}
              options={options}
              value={buyerCode}
              placeholder={
                isLoading
                  ? t(`${base}.loading_team`)
                  : t(`${base}.choose_buyer`)
              }
              disabled={isLoading}
              error={buyerCodeError()}
              onChange={(code) => onChange({ ...value, buyerCode: code })}
            />
            {buyerCode !== '' && buyerCode === selfEmployeeId && (
              <p className="mt-1 text-xs text-neut-sec">
                {t(`${base}.buyer_code_hint_self`)}
              </p>
            )}
          </div>
          <Input
            name="orderedByCode"
            type="text"
            label={t(`${base}.ordered_by_code`)}
            value={value.orderedByCode}
            error={
              errors.orderedByCode ? t(`${base}.code_too_long`) : undefined
            }
            onChange={(_, code) => onChange({ ...value, orderedByCode: code })}
          />
          <Input
            name="numDaysIntransit"
            type="text"
            inputMode="numeric"
            label={t(`${base}.num_days_intransit`)}
            value={value.numDaysIntransit}
            error={
              errors.numDaysIntransit
                ? t(
                    errors.numDaysIntransit === 'too_large'
                      ? `${base}.num_days_intransit_too_large`
                      : `${base}.num_days_intransit_invalid`
                  )
                : undefined
            }
            onChange={(_, days) =>
              onChange({ ...value, numDaysIntransit: days })
            }
          />
          <SearchableSelect
            name="unitType"
            label={t(`${base}.unit_type`)}
            options={unitTypeOptions}
            value={unitType}
            placeholder={
              isUnitTypesLoading
                ? t(`${base}.loading_unit_types`)
                : t(`${base}.choose_unit_type`)
            }
            disabled={isUnitTypesLoading || unitTypeOptions.length === 0}
            onChange={(_, code) => onChange({ ...value, unitType: code })}
          />
          <Input
            name="readyAtMill"
            type="date"
            label={t(`${base}.ready_at_mill`)}
            min={getLocalDateString()}
            value={value.readyAtMill}
            error={
              errors.readyAtMill
                ? t(`${base}.ready_at_mill_in_past`)
                : undefined
            }
            onChange={(_, readyAtMill) => onChange({ ...value, readyAtMill })}
          />
          <div className="flex items-end gap-6 pb-3">
            <Switch
              checked={value.cashContract}
              onChange={(cashContract) => onChange({ ...value, cashContract })}
              label={t(`${base}.cash_contract`)}
              dataTestId="msr-cash-contract"
            />
            <Switch
              checked={value.isImport}
              onChange={(isImport) =>
                onChange(
                  isImport
                    ? { ...value, isImport }
                    : { ...value, isImport, incoTerms: '', incoTermsLoc: '' }
                )
              }
              label={t(`${base}.is_import`)}
              dataTestId="msr-is-import"
            />
          </div>
          {value.isImport && (
            <>
              <SearchableSelect
                name="incoTerms"
                label={t(`${base}.inco_terms`)}
                options={incoTermOptions}
                value={incoTermCode || INCO_TERMS_NONE}
                placeholder={
                  isIncoTermsLoading
                    ? t(`${base}.loading_inco_terms`)
                    : t(`${base}.choose_inco_terms`)
                }
                disabled={isIncoTermsLoading || incoTerms.length === 0}
                onChange={(_, code) => pickIncoTerm(code)}
              />
              <Input
                name="incoTermsLoc"
                type="text"
                label={t(`${base}.inco_terms_loc`)}
                value={value.incoTermsLoc}
                placeholder={
                  incoTerm != null
                    ? t(
                        `${base}.inco_terms_loc_default.${incoTerm.defaultLocation}`
                      )
                    : undefined
                }
                disabled={incoTerm == null}
                error={
                  errors.incoTermsLoc
                    ? t(`${base}.inco_terms_loc_too_long`)
                    : undefined
                }
                onChange={(_, incoTermsLoc) =>
                  onChange({ ...value, incoTermsLoc })
                }
              />
            </>
          )}
        </div>
      </SectionPanel>
    </div>
  );
}
